import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, OnDestroy, computed, inject, signal } from '@angular/core';
import { ProductCard } from '../../../../shared/product-card/product-card';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../../core/api/api.config';
import {
  FrontendBlog,
  FrontendBlogSilo,
  FrontendProduct,
  FrontendStore,
} from '../../../../core/api/api.models';
import { blogBodyText, blogReadMinutes } from '../../../../core/api/blog-text';
import { SavingArenaApiService } from '../../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../../core/seo/seo.service';
import { SsrResponseService } from '../../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../../core/seo/structured-data';
import { Breadcrumb } from '../../../../shared/breadcrumb/breadcrumb';
import { BlogPostCard } from '../../../../shared/blog-post-card/blog-post-card';
import { SectionHeading } from '../../../../shared/section-heading/section-heading';


interface SiloProductView {
  id: string;
  slug: string;
  title: string;
  description: string;
  imageUrl: string;
  hoverImageUrl: string;
  oldPrice: string;
  newPrice: string;
  storeName: string;
}

interface SiloStoreView {
  id: string;
  name: string;
  slug: string;
  domain: string;
  logoUrl: string;
  coverUrl: string;
  backgroundColor: string;
  description: string;
}

interface SiloBlogView {
  id: string;
  name: string;
  slug: string;
  shortDescription: string;
  imageUrl: string;
  categoryName: string;
  categorySlug: string;
  readTime: string;
}

interface RelevantSiloView {
  id: string;
  name: string;
  slug: string;
  shortContent: string;
  totalBlogs: number | null;
}

@Component({
  selector: 'app-blog-silos-page',
  imports: [Breadcrumb, RouterLink, TrustedCmsHtmlPipe, ProductCard, BlogPostCard, SectionHeading],
  templateUrl: './blog-silos-page.html',
  styleUrl: './blog-silos-page.scss',
})
export class BlogSilosPage implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly document = inject(DOCUMENT);

  protected readonly silo = signal<FrontendBlogSilo | null>(null);

  // True while the hub in the URL is being fetched. The page used to keep showing the PREVIOUS hub
  // (or nothing at all) until the response landed; it now shows a skeleton instead.
  protected readonly loading = signal(false);
  private requestedSlug = '';

  /**
   * The hero's primary button. A hub whose posts belong to an event links to that event's page,
   * where every one of its coupons is listed; otherwise it jumps to the hub's featured stores; with
   * neither, there is no button rather than a link to nowhere (it used to be `href="#"`).
   */
  protected readonly dealsLink = computed<{ label: string; path?: string[]; section?: string } | null>(() => {
    const event = this.silo()?.dealsEvent;
    if (event && event.slug) {
      const name = this.stripHtml(event.name || '').replace(/\s+sale$/i, '').trim();
      return { path: ['/event', event.slug], label: name ? `See All ${name} Coupons` : 'See All Coupons' };
    }
    if (this.stores().length > 0) {
      return { section: 'hub-stores', label: 'See Featured Stores' };
    }
    return null;
  });

  protected readonly crumbs = computed(() => {
    const silo = this.silo();
    const label = silo?.name || silo?.slug || '';
    return breadcrumbTrail(
      { label: 'Blog', path: '/blogs' },
      { label: 'Topics', path: '/blogs/hubs' },
      ...(label ? [{ label }] : []),
    );
  });
  protected readonly currentIndex = signal(0);
  protected readonly isCarouselFading = signal(false);

  private transitionTimerId: number | null = null;

  protected readonly hasName = computed(() => this.hasText(this.silo()?.name));
  protected readonly hasHeading = computed(() => this.hasText(this.silo()?.headingH1));
  protected readonly hasShortContent = computed(() => this.hasText(this.silo()?.shortContent));
  protected readonly hasLongContent = computed(() => this.hasText(this.silo()?.longContent));
  protected readonly headingParts = computed(() =>
    this.splitLastWord(this.silo()?.headingH1 ?? ''),
  );
  protected readonly heroFeatures = computed(() =>
    this.stringArray(this.silo()?.heroBanner?.features),
  );
  protected readonly heroProducts = computed(() =>
    this.toProductViews(this.silo()?.heroBanner?.products),
  );
  protected readonly stores = computed(() => this.toStoreViews(this.silo()?.stores));
  protected readonly products = computed(() => this.toProductViews(this.silo()?.products, true));
  protected readonly blogs = computed(() => this.toBlogViews(this.silo()?.blogs));
  protected readonly relevantSilos = computed(() =>
    this.toRelevantSiloViews(this.silo()?.relevantSilos),
  );
  protected readonly currentSlide = computed(() => {
    const products = this.heroProducts();

    return products[this.currentIndex()] ?? products[0] ?? null;
  });

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const siloSlug = params.get('siloSlug') ?? '';

      this.requestedSlug = siloSlug;
      this.silo.set(null);

      if (!siloSlug) {
        this.loading.set(false);
        this.applySiloSeo(null, siloSlug);
        return;
      }

      this.loading.set(true);
      this.loadSilo(siloSlug);
    });
  }

  /**
   * Title / description / canonical / schema for a blog silo, applied where the silo resolves so the
   * tags are already correct when the server serialises the HTML (spec §5). An unknown silo slug is
   * a real 404, not a 200 empty page (spec §7).
   */
  private applySiloSeo(silo: FrontendBlogSilo | null, slug: string): void {
    if (!silo) {
      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Topic not found | SavingArena',
        description: 'The blog topic you are looking for is not available on SavingArena.',
        noindex: true,
      });
      this.seo.setJsonLd([]);
      return;
    }

    const name = this.stripHtml(silo.headingH1 || silo.name || '');
    const title = (silo.metaTitle ?? '').trim() || `${name} | SavingArena`;
    const description =
      (silo.metaDescription ?? '').trim() ||
      this.stripHtml(silo.shortContent ?? '') ||
      `Explore ${name} guides, stores and product picks curated by SavingArena.`;

    const path = `/blogs/${silo.slug || slug}`;
    const url = this.seo.absolute(path);

    this.seo.apply({ title, description, canonicalPath: path });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: title,
        description,
        dateModified: typeof silo.updatedAt === 'string' ? silo.updatedAt : undefined,
      }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ]);
  }

  protected changeSlide(direction: number): void {
    const totalSlides = this.heroProducts().length;

    if (totalSlides <= 1) {
      return;
    }

    if (this.transitionTimerId !== null) {
      window.clearTimeout(this.transitionTimerId);
    }

    this.isCarouselFading.set(true);
    this.transitionTimerId = window.setTimeout(() => {
      this.currentIndex.set((this.currentIndex() + direction + totalSlides) % totalSlides);
      this.isCarouselFading.set(false);
      this.transitionTimerId = null;
    }, 200);
  }

  protected truncateText(value: string, maxLength: number): string {
    if (value.length <= maxLength) {
      return value;
    }

    return `${value.slice(0, maxLength).trimEnd()}...`;
  }

  ngOnDestroy(): void {
    if (this.transitionTimerId !== null) {
      window.clearTimeout(this.transitionTimerId);
    }
  }

  private loadSilo(siloSlug: string): void {
    this.api
      .getBlogSilo(siloSlug)
      .pipe(
        catchError((error: unknown) => {
          this.ssrResponse.noteFetchFailure(error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        // A response for a hub the visitor has already left must not overwrite the current one.
        if (siloSlug !== this.requestedSlug) {
          return;
        }

        const silo = response?.success ? response.data : null;

        this.currentIndex.set(0);
        this.silo.set(silo);
        this.loading.set(false);
        this.applySiloSeo(silo, siloSlug);
      });
  }

  /**
   * In-page jump for the hero buttons. A bare `href="#id"` resolves against `<base href="/">` and
   * would load the home page, so the link carries the full path and this scrolls in place instead.
   */
  protected scrollToSection(event: Event, id: string): void {
    const target = this.document.getElementById(id);
    if (!target) {
      return;
    }

    event.preventDefault();
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  private toProductViews(value: unknown, truncateDescription = false): SiloProductView[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((item): item is FrontendProduct => this.isRecord(item) && this.hasText(item.name))
      .map((product) => {
        const description = this.stripHtml(product.description ?? '');
        const imagePath = product.thumbnailImagePath || product.galleryImagePaths?.[0];

        return {
          id: product.id,
          slug: product.slug || this.slugify(product.title || product.name),
          title: product.title || product.name,
          description: truncateDescription ? this.truncateText(description, 100) : description,
          imageUrl: this.assetUrl(imagePath),
          hoverImageUrl: this.toHoverImageUrl(product, imagePath),
          oldPrice: product.oldPrice ?? '',
          newPrice: product.newPrice ?? '',
          storeName: product.store?.name ?? '',
        };
      });
  }

  private toHoverImageUrl(product: FrontendProduct, mainImagePath: string | undefined): string {
    const alternate = (product.galleryImagePaths ?? []).find(
      (path) => path && path !== mainImagePath,
    );

    return alternate ? this.assetUrl(alternate) : '';
  }

  private toStoreViews(value: FrontendStore[] | undefined): SiloStoreView[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((store) => this.hasText(store.name))
      .map((store) => {
        const description = this.stripHtml(
          this.stringFromUnknown(store['description']) ||
            store.topPickShortDescription ||
            this.stringFromUnknown(store['shortDescription']),
        );

        return {
          id: store.id,
          name: store.name,
          slug: store.slug,
          domain: store.domain ?? '',
          logoUrl: this.assetUrl(store.logoImagePath),
          coverUrl: this.assetUrl(store.thumbnailImagePath),
          backgroundColor: store.topPickImageBackgroundColor || '#f3e8ff',
          // Not truncated here, the same as the home page's copy of this card: the blurb is clamped
          // to three lines in CSS instead. Cutting at 100 characters put an ellipsis mid-card even
          // when three lines had room for more.
          description,
        };
      });
  }

  private toBlogViews(value: FrontendBlog[] | undefined): SiloBlogView[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((blog) => this.hasText(blog.name) && this.hasText(blog.slug))
      .map((blog) => {
        const categoryName = blog.category?.name ?? '';
        const categorySlug = blog.category?.slug ?? '';

        return {
          id: blog.id,
          name: blog.name,
          slug: blog.slug,
          shortDescription: blog.shortDescription || this.excerptFromContent(blogBodyText(blog)),
          imageUrl: this.assetUrl(blog.thumbnailImagePath),
          categoryName,
          categorySlug,
          readTime: `${this.readTimeMinutes(blog)} Min`,
        };
      });
  }

  private toRelevantSiloViews(value: FrontendBlogSilo[] | undefined): RelevantSiloView[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((silo) => this.hasText(silo.name) && this.hasText(silo.slug))
      .map((silo) => ({
        id: silo.id,
        name: silo.name,
        slug: silo.slug,
        shortContent: this.stripHtml(silo.shortContent ?? ''),
        totalBlogs: typeof silo.totalBlogs === 'number' ? silo.totalBlogs : null,
      }));
  }

  private stringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter((item): item is string => this.hasText(item));
  }

  private splitLastWord(value: string): { before: string; last: string } {
    const trimmed = value.trim();
    const parts = trimmed.split(/\s+/);

    if (parts.length <= 1) {
      return { before: trimmed, last: '' };
    }

    const last = parts.pop() ?? '';

    return { before: parts.join(' '), last };
  }

  private excerptFromContent(value?: string): string {
    const text = this.stripHtml(value ?? '');

    if (!text) {
      return '';
    }

    return this.truncateText(text, 150);
  }

  private readTimeMinutes(blog: FrontendBlog): number {
    return blogReadMinutes(blog);
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private assetUrl(path: string | undefined): string {
    if (!path) {
      return '';
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private stringFromUnknown(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private hasText(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private isRecord(value: unknown): value is FrontendProduct {
    return value !== null && typeof value === 'object';
  }
}
